import type { ReactElement } from "react";
import { Datagrid, FunctionField, TextField, useListContext } from "react-admin";
import { useMediaQuery } from "@mui/material";
import type { Theme } from "@mui/material";
import { useNavigate } from "react-router";
import _max from "lodash/max";
import _min from "lodash/min";
import _sum from "lodash/sum";
import { Column } from "../../misc/Column";
import { LoadingIndicator } from "../../misc/LoadingIndicator";
import { TimeOfDayRegex } from "../../model/TimeOfDay";
import type { Weekday } from "../../model/Weekday";
import type { Timetable } from "../../providers/timetablesProvider";
import { TimetableMobileListItem } from "./TimetableMobileListItem";

const weekdays: { label: string; value: Weekday }[] = [
  { label: "Mo", value: "Monday" },
  { label: "Di", value: "Tuesday" },
  { label: "Mi", value: "Wednesday" },
  { label: "Do", value: "Thursday" },
  { label: "Fr", value: "Friday" },
  { label: "Sa", value: "Saturday" },
  { label: "So", value: "Sunday" },
];

export function TimetablesDataGrid(props: { empty?: ReactElement }) {
  const { empty } = props;
  const { isLoading, data, total } = useListContext<Timetable>();
  const navigate = useNavigate();
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  if (isLoading) {
    return <LoadingIndicator />;
  }

  if (isMobile) {
    if (!data || data.length === 0 || total === 0) {
      return empty ?? null;
    }

    return (
      <Column sx={{ gap: 1, py: "1px" }}>
        {data.map((timetable) => (
          <TimetableMobileListItem
            key={timetable.id}
            timetable={timetable}
            weekdaySummaries={weekdays
              .map(({ label, value }) => ({ label, summary: renderWeekdaySummary(timetable, value) }))
              .filter(({ summary }) => summary)}
            onClick={() => navigate(`/timetables/${timetable.id}`)}
          />
        ))}
      </Column>
    );
  }

  return (
    <Datagrid rowClick="edit" bulkActionButtons={false} empty={empty}>
      <TextField label="Name" source="name" />
      {weekdays.map(({ label, value }) => (
        <FunctionField
          key={value}
          label={label}
          render={(timetable: Timetable) => renderWeekdaySummary(timetable, value)}
          sortable={false}
        />
      ))}
    </Datagrid>
  );
}

const numberFormat = new Intl.NumberFormat("de", { minimumIntegerDigits: 2, maximumFractionDigits: 0 });

function renderWeekdaySummary(timetable: Timetable, weekday: Weekday) {
  const lessons = timetable.lessonsSchedule
    .filter((tuple) => tuple[0] === weekday)
    .map((tuple) => {
      const fromMatch = TimeOfDayRegex.exec(tuple[1]);
      if (!fromMatch) {
        throw new Error(`Invalid TimeOfDay: ${JSON.stringify(tuple[1])}`);
      }
      const toMatch = TimeOfDayRegex.exec(tuple[2]);
      if (!toMatch) {
        throw new Error(`Invalid TimeOfDay: ${JSON.stringify(tuple[2])}`);
      }
      const from = parseInt(fromMatch[1], 10) * 60 + parseInt(fromMatch[2], 10);
      const to = parseInt(toMatch[1], 10) * 60 + parseInt(toMatch[2], 10);
      const lessonUnits = (to - from) / 45;
      return { from, to, lessonUnits };
    });
  if (lessons.length === 0) {
    return "";
  }
  const from = _min(lessons.map(({ from }) => from))!;
  const to = _max(lessons.map(({ to }) => to))!;
  const lessonUnits = _sum(lessons.map(({ lessonUnits }) => lessonUnits));
  const fromMinutes = from % 60;
  const fromHours = (from - fromMinutes) / 60;
  const toMinutes = to % 60;
  const toHours = (to - toMinutes) / 60;
  return `${numberFormat.format(fromHours)}:${numberFormat.format(fromMinutes)} - ${numberFormat.format(toHours)}:${numberFormat.format(toMinutes)} (${lessonUnits} UE)`;
}
